import translations from "@/lib/translations.json";
import { cn } from "@/lib/utils";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  FileText,
  Grid2x2,
  HeartPulse,
  List,
  Lock,
  LogIn,
  LogOut,
  Moon,
  Pill,
  Plus,
  SlidersHorizontal,
  Sun,
  TriangleAlert,
  Wallet,
  CalendarX,
  Ban,
} from "lucide-react";
import type { ReactNode } from "react";
import {
  Badge,
  Group,
  IconChip,
  ListRow,
  ScreenHeader,
  SectionLabel,
  type Tone,
} from "./phone-ui";

// Static screens of the employee app, rebuilt from the real components
// (AgendaView, CurrentShiftCard, ActivityView, PaymentCycleDetailView,
// AusenciasView). Names, times and amounts are sample data.

type ScreensCopy = (typeof translations)["es"]["website"]["mobileApp"]["screens"];

const SHIFT_DAYS = new Set([1, 2, 5, 6, 7, 8, 13, 14, 16, 19, 20, 21, 26, 27, 28]);
const ABSENCE_DAYS = new Set([12]);
const SELECTED_DAY = 27;

// October 2026 starts on a Thursday; the grid runs Monday to Sunday.
const CALENDAR_CELLS: { day: number; inMonth: boolean }[] = [
  ...[28, 29, 30].map((day) => ({ day, inMonth: false })),
  ...Array.from({ length: 31 }, (_, i) => ({ day: i + 1, inMonth: true })),
  { day: 1, inMonth: false },
];

const SCREEN = "flex min-h-full flex-col";
const BODY = "flex flex-col gap-3 px-4 pb-6";

export function AgendaScreen({ t }: { t: ScreensCopy["agenda"] }) {
  return (
    <div className={SCREEN}>
      <ScreenHeader title={t.title} />
      <div className={BODY}>
        <div className="flex rounded-lg bg-[#e5e7eb] p-1">
          <span className="flex h-7 flex-1 items-center justify-center gap-1.5 rounded-md bg-white text-sm font-medium text-[#333] shadow-sm">
            <Grid2x2 size={16} aria-hidden />
            {t.calendar}
          </span>
          <span className="flex h-7 flex-1 items-center justify-center gap-1.5 text-sm font-medium text-[#808080]">
            <List size={16} aria-hidden />
            {t.list}
          </span>
        </div>

        <div className="rounded-xl border border-[#e5e7eb] bg-white p-3">
          <div className="mb-2 flex items-center gap-1">
            <span className="flex-1 text-[15px] font-semibold capitalize text-[#333]">
              {t.month}
            </span>
            <span className="grid h-7 w-7 place-items-center text-[#808080]">
              <ChevronLeft size={16} aria-hidden />
            </span>
            <span className="grid h-7 w-7 place-items-center text-[#808080]">
              <ChevronRight size={16} aria-hidden />
            </span>
          </div>
          <div className="grid grid-cols-7 gap-x-1">
            {t.weekdays.map((d, i) => (
              <span
                key={i}
                className="pb-1 text-center text-[10px] font-semibold text-[#808080]"
              >
                {d}
              </span>
            ))}
            {CALENDAR_CELLS.map(({ day, inMonth }, i) => {
              const selected = inMonth && day === SELECTED_DAY;
              const absence = inMonth && ABSENCE_DAYS.has(day);
              const shift = inMonth && SHIFT_DAYS.has(day);
              return (
                <span
                  key={i}
                  className={cn(
                    "flex aspect-square flex-col items-center justify-center rounded-lg text-sm tabular-nums",
                    inMonth ? "text-[#333]" : "text-[#808080]/45",
                    selected && "bg-[#ffa514] font-semibold text-white",
                  )}
                >
                  {day}
                  <i
                    className={cn(
                      "mt-0.5 block rounded-full",
                      absence ? "h-[3px] w-3 bg-[#ef4444]" : "h-1 w-1",
                      !absence && shift && (selected ? "bg-white" : "bg-[#ffa514]"),
                    )}
                  />
                </span>
              );
            })}
          </div>
        </div>

        <SectionLabel aside={t.oneShift}>
          <span className="uppercase">{t.selectedDay}</span>
        </SectionLabel>
        <Group>
          <ListRow
            chip={
              <IconChip tone="yellow">
                <Sun size={16} aria-hidden />
              </IconChip>
            }
            title={t.shiftTitle}
            subtitle={
              <>
                <Clock size={14} aria-hidden />
                <span className="tabular-nums">{t.shiftTime}</span>
              </>
            }
            trailing={<Badge tone="yellow">{t.shiftBand}</Badge>}
            chevron
          />
        </Group>
        <Group>
          <ListRow
            chip={
              <IconChip tone="gray">
                <CalendarX size={16} aria-hidden />
              </IconChip>
            }
            title={t.shortcutTitle}
            subtitle={t.shortcutSubtitle}
            chevron
          />
        </Group>
      </div>
    </div>
  );
}

function MarkRow({
  icon,
  label,
  scheduled,
  actual,
  variance,
  labels,
  onTime = false,
}: {
  icon: ReactNode;
  label: string;
  scheduled: string;
  actual: string;
  variance: string;
  labels: { scheduled: string; actual: string; variance: string };
  onTime?: boolean;
}) {
  const head = "text-[9.6px] font-semibold uppercase tracking-wide text-[#808080]";
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-1.5 text-sm font-semibold text-[#333]">
        {icon}
        {label}
      </div>
      <div className="grid grid-cols-3 gap-1 text-center">
        <div>
          <div className={head}>{labels.scheduled}</div>
          <div className="mt-0.5 text-lg font-extrabold tabular-nums text-[#333]">
            {scheduled}
          </div>
        </div>
        <div>
          <div className={head}>{labels.actual}</div>
          <div
            className={cn(
              "mt-0.5 text-lg font-extrabold tabular-nums",
              actual === "—" ? "text-[#808080]" : "text-[#333]",
            )}
          >
            {actual}
          </div>
        </div>
        <div>
          <div className={head}>{labels.variance}</div>
          <div
            className={cn(
              "mt-0.5 text-base font-bold tabular-nums",
              onTime ? "text-[#009900]" : "text-[#808080]",
            )}
          >
            {variance}
          </div>
        </div>
      </div>
    </div>
  );
}

export function TodayScreen({ t }: { t: ScreensCopy["today"] }) {
  const labels = {
    scheduled: t.scheduled,
    actual: t.actual,
    variance: t.variance,
  };
  return (
    <div className={SCREEN}>
      <ScreenHeader title={t.title} />
      <div className="flex flex-col gap-6 px-4 pb-6">
        <section>
          <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#808080]">
            {t.current}
          </div>
          <div className="flex flex-col gap-3">
            <div className="rounded-2xl bg-[#fff1db] p-4">
              <div className="flex items-center gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-[#ffa514] font-bold text-white">
                  {t.clientInitials}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-sm text-[#808080] opacity-80">
                    {t.clientLabel}
                  </div>
                  <div className="truncate text-lg font-semibold text-[#333]">
                    {t.clientName}
                  </div>
                </div>
                <Badge tone="green">{t.status}</Badge>
              </div>
              <div className="mt-3 flex flex-col gap-2.5 rounded-xl bg-white p-3">
                <MarkRow
                  icon={<LogIn size={14} aria-hidden />}
                  label={t.checkIn}
                  scheduled="7:00"
                  actual="7:00"
                  variance={t.onTime}
                  labels={labels}
                  onTime
                />
                <div className="h-px bg-slate-100" />
                <MarkRow
                  icon={<LogOut size={14} aria-hidden />}
                  label={t.checkOut}
                  scheduled="15:00"
                  actual="—"
                  variance="—"
                  labels={labels}
                />
              </div>
            </div>

            <div className="relative -mx-4 overflow-hidden bg-[#2c3644] py-6">
              <div className="relative flex h-40 items-center justify-center">
                <div className="absolute inset-0 opacity-30 [background-image:linear-gradient(rgba(255,255,255,.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.05)_1px,transparent_1px)] [background-size:34px_34px]" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/50" />
                <div className="absolute h-28 w-28 rounded-full border border-white/10" />
                <div className="absolute h-14 w-14 rounded-full border border-white/10" />
                <div className="absolute h-40 w-40 rounded-full border-2 border-dashed border-[rgba(255,168,28,0.65)] bg-[rgba(255,168,28,0.12)]" />
                <div className="absolute h-16 w-16 animate-radar-pulse rounded-full border border-white/30 motion-reduce:animate-none" />
                <div className="absolute h-2.5 w-2.5 rounded-full bg-white" />
                <div className="absolute right-[38%] top-[38%] h-4 w-4">
                  <div className="absolute left-1/2 top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/25 blur-sm" />
                  <div className="absolute inset-0 rounded-full border-2 border-white bg-white" />
                </div>
              </div>
              <div className="absolute inset-x-4 bottom-3 flex gap-1.5">
                {[t.zone, t.gps].map((label) => (
                  <span
                    key={label}
                    className="rounded-md border border-white/25 bg-white/15 px-2 py-0.5 text-xs font-semibold leading-4 text-white backdrop-blur-sm"
                  >
                    {label}
                  </span>
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-[#e5e7eb] bg-white p-4">
              <div className="flex items-center justify-between">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[#808080]">
                  {t.details}
                </span>
                <Badge tone="green" className="gap-1.5">
                  <Check size={12} aria-hidden />
                  {t.inRange}
                </Badge>
              </div>
              <div className="mt-2.5 grid grid-cols-2 divide-x divide-[#e5e7eb] border-t border-[#e5e7eb] pt-2.5 text-center">
                <div>
                  <div className="text-sm uppercase text-[#808080]">
                    {t.timeToRegister}
                  </div>
                  <div className="text-2xl font-bold tabular-nums text-[#333]">
                    9:41
                  </div>
                </div>
                <div>
                  <div className="text-sm uppercase text-[#808080]">
                    {t.distance}
                  </div>
                  <div className="text-2xl font-bold tabular-nums text-[#333]">
                    18 m
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-center gap-4 rounded-xl border border-[#ffa514] bg-[#ffb133] px-4 py-5 text-lg font-bold leading-tight text-[#4d2f00]">
              <LogOut size={20} aria-hidden />
              {t.markOut}
            </div>
          </div>
        </section>

        <section>
          <div className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#808080]">
            {t.todayShifts}
          </div>
          <Group>
            <ListRow
              chip={
                <IconChip tone="amber">
                  <Clock size={16} aria-hidden />
                </IconChip>
              }
              title={t.clientName}
              subtitle={t.shiftSubtitle}
              trailing={<Badge tone="amber">{t.status}</Badge>}
              chevron
            />
          </Group>
        </section>
      </div>
    </div>
  );
}

const RECORD_ICONS = [HeartPulse, Pill, FileText] as const;

export function RecordsScreen({ t }: { t: ScreensCopy["records"] }) {
  return (
    <div className={SCREEN}>
      <ScreenHeader title={t.title} />
      <div className="flex flex-col gap-2 px-4 pb-6">
        <div className="mt-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-[#808080]">
          {t.forToday}
        </div>
        {t.items.map((item, i) => {
          const Icon = RECORD_ICONS[i] ?? FileText;
          return (
            <Group key={item.title}>
              <ListRow
                chip={
                  <IconChip tone="ac">
                    <Icon size={16} aria-hidden />
                  </IconChip>
                }
                title={item.title}
                subtitle={item.subtitle}
                chevron
              />
            </Group>
          );
        })}
        <Group>
          <ListRow
            chip={
              <IconChip tone="green">
                <Check size={16} aria-hidden />
              </IconChip>
            }
            title={t.done.title}
            subtitle={t.done.subtitle}
            muted
          />
        </Group>

        <div className="mt-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-[#808080]">
          {t.history}
        </div>
        {t.historyItems.map((item, i) => {
          const Icon = RECORD_ICONS[i === 0 ? 0 : 2];
          return (
            <Group key={item.title}>
              <ListRow
                chip={
                  <IconChip tone="neutral">
                    <Icon size={16} aria-hidden />
                  </IconChip>
                }
                title={item.title}
                subtitle={item.subtitle}
                trailing={
                  i === 0 ? (
                    <TriangleAlert
                      size={14}
                      className="shrink-0 text-[#d97706]"
                      aria-hidden
                    />
                  ) : undefined
                }
                chevron
              />
            </Group>
          );
        })}
      </div>
    </div>
  );
}

export function PaymentsScreen({ t }: { t: ScreensCopy["payments"] }) {
  return (
    <div className={SCREEN}>
      <ScreenHeader title={t.title} back />
      <div className="flex flex-col gap-3 px-4 pb-6">
        <div className="rounded-2xl border border-[#e5e7eb] bg-white p-3.5">
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-semibold uppercase tracking-[0.05em] text-[#333]">
              {t.yourPay}
            </span>
            <span className="rounded-md bg-[#fde68a] px-2 py-[3px] text-[0.68rem] font-semibold leading-4 text-[#78350f]">
              {t.status}
            </span>
          </div>
          <div className="mb-0.5 mt-1.5 text-[1.95rem] font-semibold leading-tight tracking-[-0.03em] text-[#333]">
            {t.amount}
          </div>
          <div className="mt-2.5 border-t border-[#e5e7eb] pt-1.5">
            <div className="flex justify-between gap-3 text-[0.82rem] leading-5 text-[#333]">
              <span className="opacity-80">{t.estimated}</span>
              <span className="font-semibold">{t.estimatedDate}</span>
            </div>
          </div>
        </div>
        <div className="px-1 text-right text-xs font-semibold text-[#d97706]">
          {t.more}
        </div>

        <div className="flex flex-col gap-2">
          <SectionLabel>{t.accounts}</SectionLabel>
          <Group>
            <ListRow
              chip={
                <IconChip tone="gray">
                  <Wallet size={16} aria-hidden />
                </IconChip>
              }
              title={t.accountName}
              subtitle={t.accountDetail}
              trailing={
                <Lock size={16} className="shrink-0 text-[#808080]" aria-hidden />
              }
            />
          </Group>
        </div>

        <div className="flex flex-col gap-2">
          <SectionLabel aside={t.shiftsCount}>{t.shifts}</SectionLabel>
          <Group>
            {t.rows.map((row) => (
              <ListRow
                key={row.title}
                title={row.title}
                titleSuffix={
                  <span className="text-xs font-medium text-[#808080]">
                    {" "}
                    - {row.hours}
                  </span>
                }
                subtitle={row.subtitle}
                trailing={
                  <span className="shrink-0 text-sm font-semibold text-[#333]">
                    {row.amount}
                  </span>
                }
              />
            ))}
          </Group>
        </div>
      </div>
    </div>
  );
}

const ABSENCE_STATUS: { tone: Tone; Icon: typeof Clock }[] = [
  { tone: "warn", Icon: Clock },
  { tone: "green", Icon: Check },
  { tone: "gray", Icon: Ban },
];

export function AbsencesScreen({ t }: { t: ScreensCopy["absences"] }) {
  return (
    <div className={SCREEN}>
      <ScreenHeader title={t.title} back />
      <div className={BODY}>
        <div className="flex h-11 items-center justify-center gap-2 rounded-lg border border-[#ffa514] bg-[#ffb133] text-base font-semibold text-[#4d2f00]">
          <Plus size={16} aria-hidden />
          {t.request}
        </div>
        <div className="mt-1 px-1 text-[11px] font-semibold uppercase tracking-wide text-[#808080]">
          {t.mine}
        </div>
        <Group>
          {t.rows.map((row, i) => {
            const { tone, Icon } = ABSENCE_STATUS[i] ?? ABSENCE_STATUS[0];
            return (
              <ListRow
                key={row.title}
                chip={
                  <IconChip tone={tone}>
                    <Icon size={16} aria-hidden />
                  </IconChip>
                }
                title={row.title}
                subtitle={row.subtitle}
                trailing={
                  <Badge tone={tone} pill>
                    {row.status}
                  </Badge>
                }
                chevron={i < 2}
              />
            );
          })}
        </Group>
      </div>
    </div>
  );
}

const NIGHT_STATUS: { tone: Tone; Icon: typeof Clock }[] = [
  { tone: "green", Icon: Check },
  { tone: "warn", Icon: Clock },
  { tone: "gray", Icon: Clock },
  { tone: "gray", Icon: Clock },
  { tone: "gray", Icon: Clock },
];

export function NightScreen({ t }: { t: ScreensCopy["night"] }) {
  return (
    <div className={SCREEN}>
      <ScreenHeader title={t.title}>
        <span className="grid h-8 w-8 place-items-center text-[#808080]">
          <SlidersHorizontal size={20} aria-hidden />
        </span>
      </ScreenHeader>
      <div className={BODY}>
        <div className="overflow-hidden rounded-2xl border border-[#e5e7eb] bg-white">
          <div className="flex items-center gap-3 p-3">
            <IconChip tone="night">
              <Moon size={16} aria-hidden />
            </IconChip>
            <div className="min-w-0 flex-1">
              <div className="text-base font-semibold leading-none text-[#333]">
                {t.shift}
              </div>
              <div className="mt-0.5 text-sm text-[#808080]">{t.shiftTime}</div>
            </div>
            <Badge tone="green">{t.status}</Badge>
          </div>
          <div className="bg-[#2b3845] px-3.5 pb-3.5 pt-4 text-center text-white">
            <div className="text-[11px] font-semibold uppercase tracking-[0.06em] opacity-70">
              {t.control}
            </div>
            <div className="mt-0.5 text-[44px] font-extrabold leading-[1.1] tracking-tight tabular-nums">
              {t.now}
            </div>
            <div className="mb-3 mt-0.5 text-[13px] font-semibold text-[#fbbf24]">
              {t.remaining}
            </div>
            <div className="flex h-12 items-center justify-center gap-2 rounded-lg border border-[#ffa514] bg-[#ffb133] text-base font-bold text-[#4d2f00]">
              <Check size={16} aria-hidden />
              {t.mark}
            </div>
            <div className="mt-2.5 text-xs leading-normal opacity-75">{t.rule}</div>
          </div>
        </div>

        <SectionLabel aside={t.progress}>{t.list}</SectionLabel>
        <Group>
          {t.rows.map((row, i) => {
            const { tone, Icon } = NIGHT_STATUS[i] ?? NIGHT_STATUS[0];
            return (
              <ListRow
                key={row.time}
                chip={
                  <IconChip tone={tone}>
                    <Icon size={16} aria-hidden />
                  </IconChip>
                }
                title={row.time}
                subtitle={row.subtitle}
                trailing={<Badge tone={tone}>{row.status}</Badge>}
              />
            );
          })}
        </Group>
      </div>
    </div>
  );
}
